import React from "react";
import {
  MdArrowOutward,
  MdCalendarMonth,
  MdDomain,
  MdGroups2,
  MdLocationOn,
  MdOutlineHandshake,
  MdOutlineInsights,
  MdOutlineRocketLaunch,
  MdVerified,
} from "react-icons/md";

import EventNavigation from "../EventNavigation/EventNavigation";
import Footer from "../Footer/Footer";
import "./apex_page.css";

const eventFacts = [
  { value: "6,000+", label: "registered attendees" },
  { value: "20+", label: "countries represented" },
  { value: "120+", label: "exhibitors" },
  { value: "150+", label: "speakers" },
];

const pillars = [
  {
    icon: MdOutlineHandshake,
    title: "Connect",
    text: "Meet investors, developers, advisors, and capital partners directly—without the layers that slow cross-border transactions.",
  },
  {
    icon: MdOutlineInsights,
    title: "Showcase",
    text: "Put pipelines, portfolios, and investment opportunities in front of a pre-qualified audience of active buyers.",
  },
  {
    icon: MdOutlineRocketLaunch,
    title: "Grow",
    text: "Move promising conversations toward action with decision-makers, legal experts, and market analysts in one room.",
  },
];

const audiences = [
  {
    title: "Institutional capital",
    text: "Evaluate vetted residential, commercial, and industrial opportunities, engage developers raising capital, and benchmark US markets against global allocations.",
  },
  {
    title: "Private investors",
    text: "Access developers, financial advisors, and cross-border specialists for due diligence, FIRPTA compliance, LLC structuring, and mortgage solutions.",
  },
  {
    title: "Industry leaders",
    text: "Present your work, form complementary partnerships, and build relationships with buyers, brokers, architects, and PropTech innovators.",
  },
];

const coLocatedShows = [
  {
    code: "APEX",
    title: "America's Property Exhibition",
    text: "The core marketplace for real estate investment, development, and deal-making.",
  },
  {
    code: "SHF",
    title: "Smarter Home & Furnishings Expo",
    text: "Smart-home technology, IoT devices, and sustainable furnishing brands for builders and developers.",
  },
  {
    code: "SIS",
    title: "Smarter Infrastructure Summit",
    text: "Data centers, smart-city policy, urban infrastructure investment, and the future of municipal planning.",
  },
];

const ApexPage = () => (
  <>
    <EventNavigation />

    <main className="apex-page">
      <section className="apex-hero">
        <div className="apex-hero-orb apex-hero-orb-one" />
        <div className="apex-hero-orb apex-hero-orb-two" />
        <div className="apex-hero-grid" />
        <div className="apex-shell apex-hero-content">
          <div className="apex-hero-copy">
            <h1><strong>APEX</strong><em>America's Property Exhibition</em></h1>
            <p className="apex-hero-lede">The premier real estate deal-making exhibition is heading to Las Vegas.</p>
            <div className="apex-event-details" aria-label="Event details">
              <span><MdCalendarMonth aria-hidden="true" /> September 23–25, 2026</span>
              <span><MdLocationOn aria-hidden="true" /> World Market Center, Las Vegas</span>
            </div>
            <div className="apex-hero-actions">
              <a className="apex-button apex-button-primary" href="https://uspropertyexpo.com" target="_blank" rel="noopener noreferrer">
                Register for APEX <MdArrowOutward aria-hidden="true" />
              </a>
              <a className="apex-button apex-button-quiet" href="#why-apex">Why attend</a>
            </div>
          </div>
          <aside className="apex-hero-card" aria-label="APEX event summary">
            <div className="apex-hero-card-mark">04</div>
            <p>Fourth edition</p>
            <h2>Where real capital meets real deals.</h2>
            <div className="apex-hero-card-rule" />
            <span><MdVerified aria-hidden="true" /> A working floor for evaluated, negotiated, and committed opportunities.</span>
          </aside>
        </div>
      </section>

      <section className="apex-facts" aria-label="APEX by the numbers">
        <div className="apex-shell apex-facts-grid">
          {eventFacts.map((fact) => (
            <div className="apex-fact" key={fact.label}>
              <strong>{fact.value}</strong>
              <span>{fact.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="apex-section apex-introduction">
        <div className="apex-shell apex-introduction-grid">
          <div className="apex-section-heading">
            <p className="apex-kicker apex-kicker-dark">A marketplace in motion</p>
            <h2>Not a conventional conference. A place to make progress.</h2>
          </div>
          <div className="apex-prose">
            <p>Founded in New York in 2024, APEX was created for developers, global investors, institutional capital allocators, and real estate professionals actively sourcing, financing, and closing opportunities in the American property market.</p>
            <p>Across its first three editions, APEX attracted thousands of participants from more than 20 countries. Its Las Vegas edition brings that international momentum to one of America's most dynamic markets for luxury development, smart-city innovation, and high-yield property investment.</p>
          </div>
        </div>
      </section>

      <section className="apex-section apex-pillars-section">
        <div className="apex-shell">
          <div className="apex-section-intro">
            <p className="apex-kicker apex-kicker-dark">Three ways to move forward</p>
            <h2>Built around the work that gets deals done.</h2>
          </div>
          <div className="apex-pillars-grid">
            {pillars.map(({ icon: Icon, title, text }, index) => (
              <article className="apex-pillar" key={title}>
                <span className="apex-pillar-number">0{index + 1}</span>
                <div className="apex-pillar-icon"><Icon aria-hidden="true" /></div>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="apex-section apex-why" id="why-apex">
        <div className="apex-shell">
          <div className="apex-why-banner">
            <div>
              <p className="apex-kicker">Why choose APEX</p>
              <h2>A sharper route from evaluation to commitment.</h2>
            </div>
            <p>APEX concentrates the people, expertise, and live opportunities needed to turn intent into action—over three focused days.</p>
          </div>
          <div className="apex-audience-grid">
            {audiences.map((audience) => (
              <article className="apex-audience-card" key={audience.title}>
                <MdGroups2 aria-hidden="true" />
                <h3>{audience.title}</h3>
                <p>{audience.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="apex-section apex-difference">
        <div className="apex-shell apex-difference-layout">
          <div className="apex-difference-copy">
            <p className="apex-kicker apex-kicker-dark">One ticket. Three perspectives.</p>
            <h2>More than an exhibition floor.</h2>
            <p>APEX is designed for people who close deals, not people who collect brochures. Alongside live investment opportunities and developer-to-investor matchmaking, one registration provides access to two co-located shows.</p>
            <a className="apex-button apex-difference-button" href="https://www.uspropertyexpo.com/tickets" target="_blank" rel="noopener noreferrer">
              Book Tickets <MdArrowOutward aria-hidden="true" />
            </a>
          </div>
          <div className="apex-shows-stack">
            {coLocatedShows.map((show) => (
              <article className="apex-show" key={show.code}>
                <span>{show.code}</span>
                <div><h3>{show.title}</h3><p>{show.text}</p></div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="apex-section apex-location">
        <div className="apex-shell apex-location-card">
          <div className="apex-location-icon"><MdDomain aria-hidden="true" /></div>
          <div><p className="apex-kicker apex-kicker-dark">Las Vegas, Nevada</p><h2>A global stage for the next property move.</h2></div>
          <p>At World Market Center, APEX 2026 brings international investors and American opportunity together in a sophisticated setting built for high-value networking, market intelligence, and deal origination.</p>
        </div>
      </section>

      <section className="apex-closing">
        <div className="apex-shell apex-closing-inner">
          <p className="apex-kicker">September 23–25, 2026</p>
          <h2>Make Las Vegas your next market move.</h2>
          <p>Secure your APEX All Access pass and take part in every session, workshop, and networking opportunity across all three shows.</p>
          <a className="apex-button apex-button-primary" href="https://uspropertyexpo.com" target="_blank" rel="noopener noreferrer">Visit uspropertyexpo.com <MdArrowOutward aria-hidden="true" /></a>
        </div>
      </section>
    </main>

    <Footer />
  </>
);

export default ApexPage;
